/**
 * HotkeyCheatSheet — the global `?` overlay listing every bound shortcut on the
 * current screen, grouped by scope (CLAUDE.md standing rule 14, §18.6, ADR-0013).
 * Reads the live registry snapshot, so a form's keys appear automatically the
 * moment it mounts.  Disabled bindings stay listed but greyed.
 *
 * It owns its OWN capture-phase Esc / ? so it closes cleanly — the global
 * dispatcher is already suppressed while this modal is open (aria-modal), and
 * stopPropagation keeps a form's Esc from also firing underneath.
 */
import { useEffect } from 'react';
import { useHotkeyList, type HotkeyListItem } from './hotkeyStore';
import { formatCombo } from './keyCombo';
import './hotkeys.css';

/** Scope groups shown first, in this order; other groups follow alphabetically. */
const GROUP_ORDER = ['Global', 'Navigation', 'Toolbar', 'Detail'];

function groupRank(g: string): number {
  const i = GROUP_ORDER.indexOf(g);
  return i === -1 ? GROUP_ORDER.length : i;
}

export function HotkeyCheatSheet({ onClose }: { onClose: () => void }) {
  const items = useHotkeyList((s) => s.items);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === '?') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true); // capture — beats the store
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const groups = new Map<string, HotkeyListItem[]>();
  for (const it of items) {
    const arr = groups.get(it.group) ?? [];
    arr.push(it);
    groups.set(it.group, arr);
  }
  const ordered = [...groups.entries()].sort(
    (a, b) => groupRank(a[0]) - groupRank(b[0]) || a[0].localeCompare(b[0]),
  );
  for (const [, arr] of ordered) {
    arr.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  }

  return (
    <div className="hk-overlay" role="presentation" onClick={onClose}>
      <div
        className="hk-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hk-head">
          <span className="hk-title">Keyboard shortcuts</span>
          <button className="hk-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="hk-body">
          {ordered.length === 0 && <div className="hk-empty">No shortcuts on this screen.</div>}
          {ordered.map(([group, rows]) => (
            <section key={group} className="hk-group">
              <h4 className="hk-group-title">{group}</h4>
              <ul className="hk-list">
                {rows.map((r) => (
                  <li key={r.id} className={`hk-row${r.disabled ? ' hk-disabled' : ''}`}>
                    <span className="hk-label">{r.label}</span>
                    <kbd className="hk-kbd">{formatCombo(r.combo)}</kbd>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <div className="hk-foot">
          Press <kbd className="hk-kbd">?</kbd> anywhere to open this ·{' '}
          <kbd className="hk-kbd">Esc</kbd> to close
        </div>
      </div>
    </div>
  );
}
